import { Typo } from "../../Typo";

export type ToggleButtonItem = {
  id: string;
  label: string;
};

export type ToggleButtonProps<T extends ToggleButtonItem = ToggleButtonItem> = {
  items: T[];
  selectedValue: T;
  onClick?: (selectedItem: T) => void;
  splitMode?: boolean;
};

export const ToggleButton = <T extends ToggleButtonItem = ToggleButtonItem>({
  items,
  onClick,
  selectedValue,
  splitMode = false,
}: ToggleButtonProps<T>) => {
  return (
    <div
      data-testid="toggle-button-container"
      id="toggle-button-container"
      className={`flex w-full flex-row items-center rounded-xl p-1 md:w-fit ${splitMode ? "gap-x-2" : "bg-surface-tonal-default"}`}
    >
      {items.map((item) => {
        const isSelected = item.id === selectedValue?.id;
        const baseClass =
          "p-1 min-w-[36px] px-2 cursor-pointer rounded-lg flex-grow flex-1 md:flex-none";
        const selectedClass = isSelected
          ? "bg-surface-primary border border-stroke-primary text-text-primary"
          : "text-text-tertiary";

        return (
          <div
            key={item.id}
            data-testid={item.id}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={() => onClick?.(item)}
            onKeyDown={(e) => e.key === "Enter" && onClick?.(item)}
            className={`${baseClass} ${selectedClass}`}
          >
            <Typo
              data-testid={`toggle-label-${item.id}`}
              element="p"
              type="labelSM"
              smType="labelXS"
              textColor={
                isSelected ? "text-text-primary" : "text-text-tertiary"
              }
              classes="text-center"
            >
              {item.label}
            </Typo>
          </div>
        );
      })}
    </div>
  );
};
